import { Injectable } from "@angular/core";
import { LoginService } from "./login.service";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { catchError, firstValueFrom, map } from "rxjs";
import { BACKEND_URI } from "../config/config-vars";
import { BoardRead } from "../models/boardRead.model";

const BASE_URL = `${BACKEND_URI}/api/boardRead`;
const GET_ALL_URL = BASE_URL;
const DOWNLOAD_FILES_URL = `${BASE_URL}/file/download`;

interface FileDownloadDetail {
    fileName: string;
    file: ArrayBuffer;
}

interface BoardReadFetchOptions {
  pageSize?: number;
  maxPages?: number;
  cutoffDate?: Date;
  forceRefresh?: boolean;
}

@Injectable({providedIn: 'root'})
export class boardReadService {
    constructor(private http: HttpClient, private loginService: LoginService){}
    private cachedBoardReads: BoardRead[] | null = null;
    private inFlightFetch: Promise<BoardRead[]> | null = null;
    
    async getAll(options: BoardReadFetchOptions = {}): Promise<BoardRead[]> {
      const { pageSize = 200, maxPages = 200, cutoffDate, forceRefresh = false } = options;

      if (!this.loginService.isloggedIn) {
          console.error('User is not logged in');
          return [];
      }

      // Avoid serving cached data if caller requested a cutoff range
      const useCache = !cutoffDate && !forceRefresh;

      if (useCache && this.cachedBoardReads) {
          return this.cachedBoardReads;
      }

      if (useCache && this.inFlightFetch) {
          return this.inFlightFetch;
      }

      const fetchPromise = this.fetchPagedBoardReads(pageSize, maxPages, cutoffDate);
      if (useCache) {
        this.inFlightFetch = fetchPromise;
      }

      try {
          const data = await fetchPromise;
          if (useCache) {
            this.cachedBoardReads = data;
          }
          return data;
      } catch (error) {
          console.error('Error in getAll():', error);
          return useCache ? (this.cachedBoardReads || []) : [];
      } finally {
          if (useCache) {
            this.inFlightFetch = null;
          }
      }
  }

  private async fetchPagedBoardReads(pageSize: number, maxPages: number, cutoffDate?: Date): Promise<BoardRead[]> {
      const all: BoardRead[] = [];
      let page = 0;
      const cutoffTs = cutoffDate ? cutoffDate.getTime() : null;

      while (page < maxPages) {
          const url = `${GET_ALL_URL}?page=${page}&pageSize=${pageSize}`;
          console.log(`Fetching board reads page ${page} from:`, url);

          const response = await firstValueFrom(
              this.http.get<BoardRead[]>(url)
                  .pipe(
                      catchError((error: HttpErrorResponse) => {
                          console.error(`Error fetching board reads page ${page}:`, error);
                          throw error;
                      })
                  )
          );

          const items = response || [];

          const filtered = cutoffTs
            ? items.filter(item => {
                const ts = Date.parse(item?.timeStamp || '');
                return Number.isFinite(ts) ? ts >= cutoffTs : true;
              })
            : items;

          all.push(...filtered);

          const reachedCutoff = cutoffTs
            ? items.every(item => {
                const ts = Date.parse(item?.timeStamp || '');
                return Number.isFinite(ts) ? ts < cutoffTs : false;
              })
            : false;

          if (items.length < pageSize || reachedCutoff) {
              break; // last page reached or cutoff reached
          }

          page += 1;
      }

      console.log(`Total board reads loaded: ${all.length} across ${page + 1} pages`);
      return all;
  }

    async download(): Promise<FileDownloadDetail> {
        if(this.loginService.isloggedIn) {
            console.log("BoardService.download()")
            return await firstValueFrom(
                this.http.get(DOWNLOAD_FILES_URL, {
                    headers: {
                    },
                    observe: "response",
                    responseType: "arraybuffer"
                }).pipe(
                    map(response => {
                        const fileName = response.headers.get("Content-Disposition")
                            ?.split(';')[1].split('=')[1];
                        const file = response.body;
                        return {
                            fileName, file
                        };
                    })
                )
            );
        }
      }

      async updateStatsForBoardRead(boardReadId: string): Promise<any> {
    if (!this.loginService.isloggedIn) {
        console.error('User is not logged in');
        return null;
    }

    const url = `${BASE_URL}/${boardReadId}/updateStats`;
    try {
        console.log(`Calling updateStats for boardReadId: ${boardReadId}`);
        const response = await firstValueFrom(
            this.http.post(url, {}, {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' })
            }).pipe(
                catchError((error: HttpErrorResponse) => {
                    console.error('Error updating board read stats', error);
                    throw error;
                })
            )
        );
        return response;
    } catch (err) {
        console.error('Failed to update stats:', err);
        return null;
    }
}

  async updateBoardRead(boardReadId: string, payload: Partial<BoardRead>): Promise<any> {
  if (!this.loginService.isloggedIn) {
    console.error('User is not logged in');
    return null;
  }

  const url = `${BASE_URL}/${boardReadId}`;
  try {
    const response = await firstValueFrom(
      this.http.put(url, payload, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error updating board reads', error);
          throw error;
        })
      )
    );
    return response;
  } catch (err) {
    console.error('Failed to update boardReads:', err);
    return null;
  }
}


    


}
